/**
 * DesignerCanvas - Grid-based drag-and-drop canvas
 *
 * Uses dnd-kit to enable freeform element positioning within a grid.
 * The canvas is wrapped with `nodrag nopan` classes so React Flow ignores
 * drag events, allowing dnd-kit to handle them instead.
 */

import { memo, useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import type {
  DesignerElement as DesignerElementType,
  DesignerElementType as ElementType,
} from '@deep-agents/shared-types';
import { createDesignerElement } from '@deep-agents/shared-types';
import { DesignerElement } from './DesignerElement';
import { DesignerPalette } from './DesignerPalette';
import {
  DEFAULT_CELL_WIDTH,
  DEFAULT_CELL_HEIGHT,
  DEFAULT_GRID_COLS,
  DEFAULT_GRID_ROWS,
  ACTIVATION_DISTANCE,
  GRID_LINE_COLOR,
} from './constants';

interface DesignerCanvasProps {
  elements: DesignerElementType[];
  gridCols?: number;
  gridRows?: number;
  cellWidth?: number;
  cellHeight?: number;
  onElementsChange?: (elements: DesignerElementType[]) => void;
}

export const DesignerCanvas = memo(({
  elements,
  gridCols = DEFAULT_GRID_COLS,
  gridRows = DEFAULT_GRID_ROWS,
  cellWidth = DEFAULT_CELL_WIDTH,
  cellHeight = DEFAULT_CELL_HEIGHT,
  onElementsChange,
}: DesignerCanvasProps) => {
  // Track selected element
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Calculate canvas dimensions
  const canvasWidth = gridCols * cellWidth;
  const canvasHeight = gridRows * cellHeight;

  // Configure pointer sensor with activation distance to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: ACTIVATION_DISTANCE,
      },
    })
  );

  // Generate CSS gradient for grid lines
  const gridBackground = useMemo(() => {
    return `
      linear-gradient(to right, ${GRID_LINE_COLOR} 1px, transparent 1px),
      linear-gradient(to bottom, ${GRID_LINE_COLOR} 1px, transparent 1px)
    `;
  }, []);

  // Handle drag end - snap to grid and update position
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, delta } = event;
    const elementId = active.id as string;

    // Find the element being dragged
    const elementIndex = elements.findIndex((el) => el.id === elementId);
    if (elementIndex === -1) return;

    const element = elements[elementIndex];

    // Calculate new grid position from pixel delta
    const deltaCol = Math.round(delta.x / cellWidth);
    const deltaRow = Math.round(delta.y / cellHeight);

    // Calculate new position with bounds checking
    const newCol = Math.max(0, Math.min(gridCols - element.size.colSpan, element.position.col + deltaCol));
    const newRow = Math.max(0, Math.min(gridRows - element.size.rowSpan, element.position.row + deltaRow));

    // Only update if position changed
    if (newCol === element.position.col && newRow === element.position.row) return;

    // Create updated elements array
    const newElements = [...elements];
    newElements[elementIndex] = {
      ...element,
      position: { col: newCol, row: newRow },
    };

    onElementsChange?.(newElements);
  }, [elements, cellWidth, cellHeight, gridCols, gridRows, onElementsChange]);

  // Handle adding new elements
  const handleAddElement = useCallback((type: ElementType) => {
    // Find an empty spot (simple: just stack at 0,0)
    // A smarter algorithm would find the first empty grid cell
    const newElement = createDesignerElement(type);
    const newElements = [...elements, newElement];
    onElementsChange?.(newElements);
    setSelectedId(newElement.id);
  }, [elements, onElementsChange]);

  // Handle selecting an element
  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  // Handle updating element props
  const handleUpdateElement = useCallback((id: string, props: Partial<DesignerElementType['props']>) => {
    const elementIndex = elements.findIndex((el) => el.id === id);
    if (elementIndex === -1) return;

    const element = elements[elementIndex];
    const newElements = [...elements];
    newElements[elementIndex] = {
      ...element,
      props: { ...element.props, ...props },
    } as DesignerElementType;

    onElementsChange?.(newElements);
  }, [elements, onElementsChange]);

  // Deselect when clicking canvas background
  const handleCanvasClick = useCallback(() => {
    setSelectedId(null);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {/* Palette toolbar */}
      <div className="flex justify-center">
        <DesignerPalette onAddElement={handleAddElement} />
      </div>

      {/* Canvas area with nodrag nopan to let dnd-kit handle events */}
      <div
        className="nodrag nopan relative rounded-lg border border-slate-200 overflow-hidden"
        style={{
          width: canvasWidth,
          height: canvasHeight,
          backgroundImage: gridBackground,
          backgroundSize: `${cellWidth}px ${cellHeight}px`,
          backgroundColor: '#fafafa',
        }}
        onClick={handleCanvasClick}
      >
        {/* dnd-kit context - captures all drag events within */}
        <DndContext
          sensors={sensors}
          modifiers={[restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          {/* Render all elements */}
          {elements.map((element) => (
            <DesignerElement
              key={element.id}
              element={element}
              cellWidth={cellWidth}
              cellHeight={cellHeight}
              isSelected={element.id === selectedId}
              onSelect={handleSelect}
              onUpdate={handleUpdateElement}
            />
          ))}
        </DndContext>

        {/* Empty state */}
        {elements.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400 pointer-events-none">
            <div className="text-center">
              <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <p className="text-sm font-medium">Add elements from the toolbar above</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

DesignerCanvas.displayName = 'DesignerCanvas';
